import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { motion } from 'motion/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Send, CheckCircle, Loader2 } from 'lucide-react'
import { Button, Input, Textarea, Select } from '@/components/ui'
import { submitContactForm } from '@/lib/api'

const assessmentRequestSchema = z.object({
  firstName: z.string().min(2, 'First name is required'),
  lastName: z.string().min(2, 'Last name is required'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  company: z.string().min(2, 'Company name is required'),
  industry: z.string().min(1, 'Please select an industry'),
  message: z.string().optional(),
})

type AssessmentRequestFormData = z.infer<typeof assessmentRequestSchema>

interface TrackingContext {
  page_url: string
  referrer: string
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  utm_content: string
}

interface AssessmentRequestFormProps {
  assessmentType: string
  assessmentTitle: string
  buttonText?: string
}

const industryOptions = [
  { value: '', label: 'Select your industry' },
  { value: 'credit-union', label: 'Credit Union' },
  { value: 'bank', label: 'Bank / Financial Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' },
]

export function AssessmentRequestForm({ 
  assessmentType, 
  assessmentTitle,
  buttonText = 'Request Assessment'
}: AssessmentRequestFormProps) {
  const location = useLocation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [trackingContext, setTrackingContext] = useState<TrackingContext>({
    page_url: '',
    referrer: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: '',
  })

  // Capture tracking context on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    setTrackingContext({
      page_url: window.location.href,
      referrer: document.referrer || '',
      utm_source: searchParams.get('utm_source') || '',
      utm_medium: searchParams.get('utm_medium') || '',
      utm_campaign: searchParams.get('utm_campaign') || '',
      utm_term: searchParams.get('utm_term') || '',
      utm_content: searchParams.get('utm_content') || '',
    })
  }, [location.search])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AssessmentRequestFormData>({
    resolver: zodResolver(assessmentRequestSchema),
  })

  const onSubmit = async (data: AssessmentRequestFormData) => {
    setIsSubmitting(true)
    try {
      // Submit to API with assessment type and tracking context
      await submitContactForm({
        ...data,
        services: [assessmentType],
        message: data.message || `Requesting ${assessmentTitle}`,
        context: {
          ...trackingContext,
          assessment_type: assessmentType,
          assessment_title: assessmentTitle,
        },
      })
      setIsSubmitted(true)
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-success-500/20 flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-success-500" />
        </div>
        <h3 className="text-xl font-heading font-semibold text-white mb-2">
          Thank You!
        </h3>
        <p className="text-slate-400 mb-6">
          We've received your request and will contact you within 24 hours to schedule your {assessmentTitle.toLowerCase()}.
        </p>
        <Button onClick={() => setIsSubmitted(false)} variant="secondary" size="sm">
          Submit Another Request
        </Button>
      </motion.div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="First Name *"
          placeholder="John"
          {...register('firstName')}
          error={errors.firstName?.message}
        />
        <Input
          label="Last Name *"
          placeholder="Smith"
          {...register('lastName')}
          error={errors.lastName?.message}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Email *"
          type="email"
          placeholder="john@company.com"
          {...register('email')}
          error={errors.email?.message}
        />
        <Input
          label="Phone *"
          type="tel"
          placeholder="(555) 123-4567"
          {...register('phone')}
          error={errors.phone?.message}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Company *"
          placeholder="Your Company Name"
          {...register('company')}
          error={errors.company?.message}
        />
        <Select
          label="Industry *"
          options={industryOptions}
          {...register('industry')}
          error={errors.industry?.message}
        />
      </div>

      <Textarea
        label="Additional Information (Optional)"
        placeholder="Tell us about your current challenges or specific areas of concern..."
        {...register('message')}
        error={errors.message?.message}
      />

      <Button
        type="submit"
        size="lg"
        variant="glow"
        className="w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            {buttonText}
          </>
        )}
      </Button>

      <p className="text-xs text-slate-500 text-center">
        By submitting this form, you agree to our{' '}
        <a href="/privacy" className="text-accent-400 hover:underline">
          Privacy Policy
        </a>
        . We'll contact you within 24 hours.
      </p>
    </form>
  )
}
